import type { VNode, VNodeDirective } from 'vue';

interface LongPressHTMLElement extends HTMLElement {
	$_longPressStartEvent: (event: PointerEvent) => void;
	$_longPressTimeoutId?: NodeJS.Timeout;
	$_startPosition?: { x: number; y: number };
}

const longPressBuffer = 15;

export const directiveOption = {
	bind(el: LongPressHTMLElement, binding: VNodeDirective, vNode: VNode) {
		el.$_longPressTimeoutId = undefined;
		el.$_startPosition = null;

		const styleElementId = `v-long-press-directive-${el.id}`;

		// Check if pointer movement is within the valid range for long press
		const isValidLongPress = (event: PointerEvent) => {
			if (!el.$_startPosition)
				return false;

			const deltaY = Math.abs(event.clientY - el.$_startPosition.y);
			return deltaY <= longPressBuffer;
		};

		// Emit event helper
		const emitEvent = <T>(emitName: string, event: T) => {
			if (vNode.componentInstance)
				// Emit event on the component instance
				vNode.componentInstance.$emit(emitName, event);
			else
				// Emit event on the element
				el.dispatchEvent(new CustomEvent<T>(emitName, { detail: event }));
		};

		// Handler for preventing native context menu
		const onContextMenu = (event: MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
		};

		const onScroll = (event: Event) => {
			emitEvent('long-press-cancel', event);
			onPointerUp(null);
		};

		// Check if long press is still valid
		const onPointerMove = (event: PointerEvent) => {
			if (!isValidLongPress(event)) {
				emitEvent('long-press-cancel', event);
				onPointerUp(event);
			}
		};

		// Long press end event
		const onPointerUp = (event: PointerEvent) => {
			clearTimeout(el.$_longPressTimeoutId);
			emitEvent('long-press-finish', event);

			// Remove temporary listeners
			document.removeEventListener('pointerup', onPointerUp);
			document.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('scroll', onScroll, true);

			setTimeout(() => {
				// Windows/Android: Prevent native context menu event from firing simultaneously with long press
				document.removeEventListener('contextmenu', onContextMenu);

				// Restore text selection for iOS Safari
				document.getElementById(styleElementId)?.remove();
			}, 50);
		};

		// Handle pointer down
		const onPointerDown = (event: PointerEvent) => {
			el.$_startPosition = { x: event.clientX, y: event.clientY };
			emitEvent('long-press-start', event);

			// Add temporary listeners
			document.addEventListener('pointerup', onPointerUp);
			document.addEventListener('pointermove', onPointerMove);
			document.addEventListener('contextmenu', onContextMenu);
			window.addEventListener('scroll', onScroll, true);

			// Prevent text selection for iOS Safari
			const styleElement = document.getElementById(styleElementId) ?? document.createElement('style');
			document.head.appendChild(styleElement);

			styleElement.id = styleElementId;
			styleElement.innerHTML = 'body { user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; }';

			// Start the long press timeout
			el.$_longPressTimeoutId = setTimeout(() => {
				if (isValidLongPress(event))
					emitEvent('long-press-callback', event);
			}, binding.value);
		};

		el.$_longPressStartEvent = onPointerDown;
		el.addEventListener('pointerdown', onPointerDown);
	},
	unbind(el: LongPressHTMLElement) {
		clearTimeout(el.$_longPressTimeoutId);
		el.removeEventListener('pointerdown', el.$_longPressStartEvent);
	}
};

export default directiveOption;
